/**
 * One repository's environment in the sheet: the Repositories tab's record.
 *
 * Reads top to bottom the way setup goes: what the environment is for, the
 * needs-you box with the cause and its fix, the checklist of the last run,
 * What runs here (the record builds leave), then three groups of rows: How it
 * builds (Application, Sign-in, First check, Variables, Network), What agents
 * read (Other repositories, Skills, Description) and How it ships (Checks,
 * Pull requests, Deploys), then Advanced. The footer is the run: Set up, Run
 * again, Stop, or the fix the failed run asks for.
 *
 * Nothing here remembers a run. Everything comes back from the environment's
 * `read`, so a reload in the middle of setup reads the same. The sheet prints
 * outside the tab's tree, so the environment state comes in as a prop.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  qaChecklist,
  type QaAsk,
  type QaChecklistRow,
  type QaEnvironmentConfig,
} from "@yagni/shared";
import { Button } from "../ui/index.js";
import { buttonVariants } from "../ui/button.js";
import { Chip, MenuItem, RecordHead, SheetFoot, Stamp } from "../queue/index.js";
import { cx } from "../queue/primitives.js";
import { SettingsRow } from "../workers/SettingsRow.js";
import { EnvironmentHumanSignIn } from "../settings/EnvironmentHumanSignIn.js";
import { RepositoryDeploys } from "../settings/RepositoryDeploys.js";
import { RepositoryAgentTrust, agentTrustSummary } from "../settings/RepositoryAgentTrust.js";
import { RepositoryPrBase, prBaseSummary } from "../settings/RepositoryPrBase.js";
import {
  EnvironmentAdvanced,
  OtherRepositories,
  advancedSummary,
  otherRepositoriesSummary,
} from "../settings/EnvironmentAdvanced.js";
import {
  ApplicationFields,
  FirstCheckFields,
  NetworkFields,
  SignInFields,
  firstCheckSummary,
  networkSummary,
  signInSummary,
} from "../settings/EnvironmentConfigRows.js";
import { inspectionSummary } from "../settings/EnvironmentConfigSummary.js";
import { EnvironmentYaml } from "../settings/EnvironmentYaml.js";
import { EnvironmentFromFields, environmentFromSummary } from "../settings/EnvironmentFrom.js";
import {
  RECORDING_NOTE,
  RUN_STATUS_WORDS,
  SHORT_VALUES_NOTE,
  askHost,
  attentionMessage,
  attentionRowKey,
  canSwitchTo,
  environmentFix,
  environmentHeadline,
  environmentPill,
  failedStepLine,
  fixesTriedLine,
  formatEnvironmentDate,
  indexLine,
  isVariableName,
  lastBuildFailureLine,
  originFixPatch,
  originPatch,
  recheckedWorking,
  rowDetail,
  runAction,
  runDetails,
  runSteps,
  runSystemLine,
  stepElapsed,
  stepRanOutLine,
  checkRequirementsSummary,
  repositoryVariableRows,
  repositoryVariablesSummary,
  seedNotes,
  VALIDATE_NOW_HELD,
  VALIDATE_NOW_HELP,
  valuesToReenter,
  valuesToReenterLine,
} from "../settings/environmentPage.js";
import {
  CheckRequirements,
  RepositoryDescription,
  RepositoryRelations,
  RepositoryRole,
  relationsSummary,
  roleSummary,
} from "../settings/RepositoryIndexParts.js";
import { RepositoryVariables } from "../settings/RepositoryVariables.js";
import type { useRepositoryEnvironment } from "../settings/useRepositoryEnvironment.js";
import { useRepoAgentTrust } from "../settings/useRepoAgentTrust.js";
import { useRepoPrBase } from "../settings/useRepoPrBase.js";
import { useRepositoryDeploys } from "../settings/useRepositoryDeploys.js";
import { WhatRunsHere } from "../settings/WhatRunsHere.js";
import { useWorkspaceVariables } from "../settings/workspaceVariablesApi.js";
import { indexChip } from "./repositoriesModel.js";
import { RepositoryRunningSystem } from "./system/RunningSystem.js";
import "../workers/workers.css";
import "../settings/repositoryEnvironment.css";

export type RepositoryEnvironment = ReturnType<typeof useRepositoryEnvironment>;

/** The mark in front of a row. One character, in the machine-fact voice. */
const MARKS: Record<QaChecklistRow["state"], string> = {
  done: "✓",
  failed: "!",
  running: "·",
  pending: "○",
  skipped: "–",
  warning: "!",
};

const ROW_CLASSES: Partial<Record<QaChecklistRow["state"], string>> = {
  done: "is-ok",
  failed: "is-missing",
  running: "is-running",
  warning: "is-warning",
};

/** One credential the run asked for: its name, a write-only value, and go. */
function CredentialAsk({
  name,
  disabled,
  onSave,
}: {
  name: string;
  disabled: boolean;
  onSave: (name: string, value: string) => void;
}) {
  const [label, setLabel] = useState(name);
  const [value, setValue] = useState("");
  const ready = isVariableName(label) && value.length > 0;
  return (
    <div className="qa-ask" data-testid="qa-ask-credential">
      <input
        className="qa-ask-name"
        aria-label="Credential name"
        value={label}
        disabled={disabled}
        spellCheck={false}
        autoComplete="off"
        onChange={(event) => setLabel(event.target.value.trim())}
      />
      <input
        className="qa-ask-value"
        type="password"
        aria-label={`Value for ${label || name}`}
        value={value}
        disabled={disabled}
        autoComplete="new-password"
        onChange={(event) => setValue(event.target.value)}
      />
      <Button disabled={disabled || !ready} onClick={() => onSave(label, value)}>
        Save and run
      </Button>
    </div>
  );
}

/**
 * What the run asks a person for, each as the control that supplies it. The
 * approval stays the person's: an origin is added only when they press its
 * button, and every ask starts the same run the footer would.
 */
function EnvironmentAsks({
  asks,
  disabled,
  settingUp,
  config,
  onRun,
  onOpenRow,
}: {
  asks: QaAsk[];
  disabled: boolean;
  settingUp: boolean;
  config: QaEnvironmentConfig;
  onRun: (overrides: {
    setEnvVars?: Record<string, string>;
    patch?: Partial<QaEnvironmentConfig>;
  }) => void;
  onOpenRow: (section: string) => void;
}) {
  return (
    <div className="qa-asks" data-testid="qa-asks">
      {asks.map((ask) => {
        switch (ask.kind) {
          case "credential":
            return (
              <CredentialAsk
                key={`credential:${ask.name}`}
                name={ask.name}
                disabled={disabled}
                onSave={(name, value) => onRun({ setEnvVars: { [name]: value } })}
              />
            );
          case "origin":
            return (
              <div className="qa-ask" key={`origin:${ask.list}:${ask.origin}`}>
                <Button
                  variant="secondary"
                  disabled={disabled}
                  onClick={() => onRun({ patch: originPatch(config, ask) })}
                >
                  {ask.list === "installation"
                    ? `Allow ${askHost(ask.origin)} for installs and run`
                    : `Allow ${askHost(ask.origin)} for the app and run`}
                </Button>
              </div>
            );
          case "sign_in":
            return (
              <div className="qa-ask" key="sign_in">
                <Button
                  variant="secondary"
                  disabled={disabled}
                  onClick={() => onOpenRow("sign-in")}
                >
                  Open sign-in
                </Button>
              </div>
            );
          case "link":
            return (
              <div className="qa-ask" key={`link:${ask.href}`}>
                {ask.href.startsWith("/") ? (
                  <Link className={buttonVariants({ variant: "secondary" })} to={ask.href}>
                    {ask.label}
                  </Link>
                ) : (
                  <a
                    className={buttonVariants({ variant: "secondary" })}
                    href={ask.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {ask.label}
                  </a>
                )}
              </div>
            );
          default:
            return null;
        }
      })}
      {settingUp && (
        <span role="status" className="qa-hint">
          Setting up
        </span>
      )}
    </div>
  );
}

/** Puts a failed run's identifiers on the clipboard, for support. */
function CopyRunDetails({ text }: { text: string }) {
  const [said, setSaid] = useState<string | null>(null);
  return (
    <button
      type="button"
      className="wrk-quiet-btn"
      onClick={() => {
        const clipboard = navigator.clipboard;
        if (!clipboard) {
          setSaid("Could not copy");
          return;
        }
        clipboard.writeText(text).then(
          () => setSaid("Copied"),
          () => setSaid("Could not copy"),
        );
      }}
    >
      {said ?? "Copy run details"}
    </button>
  );
}

const PILL_KIND = { neutral: "done", executing: "live", needsYou: "needs", done: "live" } as const;

export interface RepositoryEnvironmentSheetProps {
  env: RepositoryEnvironment;
  onClose: () => void;
}

export function RepositoryEnvironmentSheet({ env, onClose }: RepositoryEnvironmentSheetProps) {
  const [openSection, setOpenSection] = useState<string | null>(null);
  // A step's "line N": the YAML editor brings that line into view and marks it.
  const [reveal, setReveal] = useState<{ line: number; at: number } | null>(null);
  const [showRuns, setShowRuns] = useState(false);
  // "Configure by hand instead" on a first visit. Per repository: another
  // repository's first visit starts folded again.
  const [byHandFor, setByHandFor] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { config, view, repo, selected, validating, validation, busy, operationBusy } = env;
  const toggle = (section: string) => () =>
    setOpenSection((current) => (current === section ? null : section));
  // A row opened from the needs-you box is usually far below it: bring it into
  // view and put the cursor in its first control.
  const [focusRequest, setFocusRequest] = useState<{ section: string; at: number } | null>(null);
  const openRow = (section: string) => {
    setOpenSection(section);
    setFocusRequest({ section, at: Date.now() });
  };
  // What runs here's Set: the Variables row, open on that name, where the
  // person chooses this repository or every repository.
  const [variableAsk, setVariableAsk] = useState<string | null>(null);
  const askVariable = (name: string) => {
    setVariableAsk(name);
    setByHandFor(selected ?? null);
    openRow("credentials");
  };
  useEffect(() => {
    if (!focusRequest) return;
    const body = document.getElementById(`wrk-setrow-body-${focusRequest.section}`);
    if (!body) return;
    (body.closest(".wrk-setrow") ?? body).scrollIntoView?.({ behavior: "smooth", block: "start" });
    (
      body.querySelector<HTMLElement>("[data-autofocus='true']:not([disabled])") ??
      body.querySelector<HTMLElement>(
        "input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])",
      )
    )?.focus({ preventScroll: true });
  }, [focusRequest]);

  const draftOrActive = view?.draft ?? view?.active;
  const savedVariables = draftOrActive?.envVars ?? [];
  const credentialNames = [
    ...new Set([...savedVariables.map((variable) => variable.name), ...env.stagedSecretNames]),
  ].filter((name) => !env.removed.includes(name));

  // The Variables row: every name this repository's builds read, each with
  // where its value comes from.
  const workspace = useWorkspaceVariables();
  const index = env.index ?? null;
  // A name whose value a disconnect cleared is saved but not set: it reads as
  // missing until a person enters it again.
  const reenter = valuesToReenter(view, env.stagedSecretNames, env.removed);
  const reenterLine = valuesToReenterLine(reenter);
  const variableRows = repositoryVariableRows({
    index,
    saved: savedVariables.map((variable) => variable.name),
    staged: env.stagedSecretNames,
    removed: env.removed,
    reenter,
    workspace: workspace.variables,
    ask: variableAsk,
    environmentFrom: config.environmentFrom
      ? {
          needs: config.environmentFrom.needs,
          repository:
            env.repositories.find((item) => item.id === config.environmentFrom!.repoId) ?? null,
        }
      : null,
  });
  const checksSummary = checkRequirementsSummary(index);
  const prBase = useRepoPrBase(selected ?? null);
  const agentTrust = useRepoAgentTrust(selected ?? null);
  const deploys = useRepositoryDeploys(selected ?? undefined);
  const description = indexLine(index);

  const pill = environmentPill(view?.state, view?.buildOnly);
  const headline = view ? environmentHeadline(view) : null;
  const rows = view ? qaChecklist(env.runConfig, validation) : [];
  const failed = validation?.status === "needs_attention";
  // A run waiting on a person to sign in is attention, not a failure: the
  // sign-in row carries the private browser rather than a cause.
  const awaitingSignIn = Boolean(
    validation?.status === "validating" && validation.interactiveSessionId,
  );
  const attentionKey = attentionRowKey(rows, validation);
  const attentionPlaced = rows.some((row) => row.key === attentionKey);
  const fix = environmentFix(validation?.needs);
  const tried = fixesTriedLine(validation?.fixes);

  const latestRun = env.latestRun;
  const newerThanWorking = Boolean(
    view?.working &&
      latestRun &&
      latestRun.revisionId !== view.working.revisionId &&
      !recheckedWorking(view, latestRun),
  );
  const canSwitch = canSwitchTo({
    view,
    latestRun,
    validation,
    dirty: env.dirty,
    busy,
    validating,
  });

  // Nothing saved is still Set up, however much of the form has been filled
  // in: there is no saved configuration for "Save and run" to be against.
  const saved = env.configured || Boolean(latestRun);
  const { label: runLabel, help: runHelp } = runAction({
    validating,
    saved,
    dirty: env.dirty,
    credentialPending: env.credentialsPending,
  });
  // First visit: nothing has ever run and nothing is saved, so the sheet is
  // one sentence, what a run records, and Set up. Configuring by hand is one
  // disclosure away.
  const startCard = Boolean(
    view && view.state === "not_set_up" && !latestRun && !view.draft && !view.active,
  );
  const byHand = !startCard || (byHandFor !== null && byHandFor === selected);

  const said = validation ? attentionMessage(validation) : undefined;
  const asks = failed ? (validation?.asks ?? []) : [];
  // Approve the destination approves it: the origins the run asked for go
  // into the draft, so Save and run sends them, and the Network row opens on
  // the list that now holds them. Every other fix only opens its row.
  const applyFix = (section: string) => {
    const patch = validation?.needs === "origin" ? originFixPatch(config, asks) : null;
    if (patch) env.edit(patch);
    openRow(section);
  };
  const buildFailure = view?.state === "working" ? view.lastBuildFailure : null;
  const buildFailureLine = lastBuildFailureLine(buildFailure);
  const notes = seedNotes(view);
  const ready = Boolean(selected && view?.repoId === selected);
  // The run's steps and what reading the repository noticed; an older server
  // sends neither.
  const steps = runSteps(validation);
  const runNotes = (validation?.notes ?? []).filter(Boolean);
  const systems = env.systems ?? [];
  const failedLine = failedStepLine(validation);
  // A system's run says whether it ran inside the system or alone.
  const systemLine = runSystemLine(validation?.system);
  // Another repository, or a new run, forgets the line a step asked to see.
  const revealKey = `${selected ?? ""}:${latestRun?.revisionId ?? ""}`;
  useEffect(() => {
    if (revealKey) setReveal(null);
  }, [revealKey]);

  /** The needs-you box: the cause, what the run tried, and what to change. */
  const attentionBlock = (failed || awaitingSignIn) && validation && (
    <div className="qa-attention" data-testid="qa-attention">
      {failed && (
        <>
          <strong className="qa-cause">{validation.cause ?? validation.message}</strong>
          {said && <span className="qa-attention-line">{said}</span>}
          {tried && <span className="qa-attention-line">{tried}</span>}
          {validation.advice && <span className="qa-attention-line">{validation.advice}</span>}
          {validation.repairAdvice && (
            <div className="flex flex-col gap-1" data-testid="qa-repair-advice">
              <span className="qa-hint">What a repair would have changed</span>
              <span className="qa-attention-line">{validation.repairAdvice.explanation}</span>
              {validation.repairAdvice.changes.length > 0 && (
                <ul className="qa-attention-line m-0 list-disc pl-5">
                  {validation.repairAdvice.changes.map((change) => (
                    <li key={change}>{change}</li>
                  ))}
                </ul>
              )}
              <span className="qa-hint">
                Nothing was changed: this environment is declared. Edit the YAML above to apply it.
              </span>
            </div>
          )}
          {validation.errorCode === "setup_unexpected" && validation.reference && (
            <span className="qa-fact">Run reference {validation.reference}</span>
          )}
          {asks.length > 0 && (
            <EnvironmentAsks
              asks={asks}
              disabled={!env.canAsk}
              settingUp={busy || validating}
              config={config}
              onRun={(overrides) => void env.run(overrides)}
              onOpenRow={openRow}
            />
          )}
          <div className="qa-attention-actions">
            {fix && (
              <Button variant="secondary" onClick={() => applyFix(fix.section)}>
                {fix.label}
              </Button>
            )}
            {validation.outputTail && (
              <details className="qa-output">
                <summary>Show output</summary>
                <pre>{validation.outputTail}</pre>
              </details>
            )}
            {latestRun && <CopyRunDetails text={runDetails(latestRun.revisionId, validation)} />}
          </div>
        </>
      )}
      {awaitingSignIn && validation.interactiveSessionId && (
        <EnvironmentHumanSignIn
          key={validation.interactiveSessionId}
          sessionId={validation.interactiveSessionId}
          onResumed={() => void env.reread()}
        />
      )}
    </div>
  );

  const teams = view?.uses?.teams ?? [];
  const freshness = indexChip(index);
  const repoName = repo ? `${repo.owner}/${repo.name}` : null;
  // What runs here (YAG-849). An older server sends no field: nothing to say.
  const capabilities = repo?.capabilities;
  const whatRunsHere =
    capabilities === undefined ? null : (
      <WhatRunsHere record={capabilities} onSetVariable={askVariable} />
    );

  async function copyLink() {
    if (!repoName) return;
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/services/repositories?repository=${encodeURIComponent(repoName)}`,
      );
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // The address bar still has the link.
    }
  }

  const more = (
    <>
      {selected && env.index !== undefined && (
        <MenuItem disabled={env.indexBusy} onSelect={() => void env.reindex(selected)}>
          Re-index the repository
        </MenuItem>
      )}
      <MenuItem onSelect={() => void copyLink()}>{copied ? "Copied" : "Copy link"}</MenuItem>
    </>
  );

  // The footer is the run. A failed run that names its fix leads with the fix
  // until the configuration has been changed; then the run is the verb again.
  const leadWithFix = Boolean(failed && fix && !env.dirty && !validating);
  const runVerb = { label: runLabel, onClick: () => void env.run(), disabled: !env.canRun };
  // A refused request is said beside the verb that sent it: the body may be
  // scrolled far from its top, where a notice would go unseen.
  const refusal = env.error && !env.verificationFailed ? env.error : undefined;
  const footer = !ready ? null : validating ? (
    <SheetFoot
      error={refusal}
      why={
        <>
          Stopping ends this run. <b>Pull request checks keep the environment that works.</b>
        </>
      }
      secondary={{ label: "Refresh status", onClick: env.refreshStatus, disabled: operationBusy }}
      primary={{
        label: "Stop",
        onClick: () => void env.stop(),
        disabled: operationBusy || !view?.draft,
      }}
    />
  ) : leadWithFix && fix ? (
    <SheetFoot
      error={refusal}
      why={
        <>
          Change what the run asked for, then run again. <b>Builds in flight keep theirs.</b>
        </>
      }
      secondary={runVerb}
      primary={{ label: fix.label, onClick: () => applyFix(fix.section) }}
    />
  ) : (
    <SheetFoot
      error={refusal}
      why={
        startCard ? (
          <>
            Set up reads the repository, starts it in a sandbox and checks its first page.{" "}
            <b>Builds use it from the next run.</b>
          </>
        ) : (
          <>{runLabel} re-checks the environment. Builds in flight keep theirs.</>
        )
      }
      secondary={{
        label: "Refresh status",
        onClick: () => void env.reread(),
        disabled: operationBusy,
      }}
      primary={runVerb}
    />
  );

  return (
    <>
      <RecordHead
        recordKey="REPOSITORY"
        title={repoName ?? "Repository"}
        onClose={onClose}
        more={more}
        meta={
          <>
            {view && (
              <span className={cx("pill", PILL_KIND[pill.tone])} data-testid="qa-state-pill">
                {(pill.pulse || pill.tone === "executing") && <span className="dot" aria-hidden />}
                {pill.label}
              </span>
            )}
            {teams.length > 0
              ? teams.map((team) => <Chip key={team.id}>{team.name}</Chip>)
              : view && <Chip>no Team</Chip>}
            {freshness && <Chip>{freshness}</Chip>}
          </>
        }
      />
      <div className="sbody qa-sheet" data-testid="repository-sheet">
        {view && headline && (
          <p role="status" className="body-p qa-head-line">
            {headline.lead}
            {headline.detail ? ` ${headline.detail}` : ""}
            {headline.attach && (
              <>
                {" "}
                <Link to={headline.attach.href}>{headline.attach.text}</Link>
              </>
            )}
          </p>
        )}

        {/*
          A verification failure is weather, not a verdict: the repository is
          still yours, GitHub just could not be reached to say so.
        */}
        {env.verificationRetryable && (
          <p
            role="status"
            className="qa-notice qa-notice-retry"
            data-testid="qa-verification-notice"
          >
            <span>
              Access to this repository could not be verified just now. Setup will not run until
              that clears.
            </span>
            <button
              type="button"
              className="wrk-quiet-btn"
              disabled={env.listBusy}
              onClick={env.retryVerification}
            >
              Try again
            </button>
          </p>
        )}
        {refusal && !footer && (
          <p role="alert" className="qa-notice">
            {refusal}
          </p>
        )}
        {env.loaded && env.repositoryName && !selected && env.repositories.length > 0 && (
          <p role="status" className="qa-empty">
            This repository is no longer available. Choose an attached repository or reconnect it in{" "}
            <Link to="/services">Connections</Link>.
          </p>
        )}

        {ready && view && (
          <>
            {view.working && newerThanWorking && (
              <p role="status" className="qa-still-in-use">
                PR checks still use run {view.working.number} from{" "}
                {formatEnvironmentDate(view.working.since)}.
              </p>
            )}
            {notes.length > 0 && (
              <p className="qa-still-in-use" data-testid="qa-seed-note">
                {notes.join(" ")}
              </p>
            )}
            {systems.length > 0 && (
              <div className="qa-systems" data-testid="qa-systems">
                <span className="qa-hint">{systems.length === 1 ? "System" : "Systems"}</span>
                {systems.map((system) => (
                  <Link key={system.id} className="chip" to="/settings/systems">
                    {system.name}
                  </Link>
                ))}
              </div>
            )}
            {reenterLine && (
              <p role="status" className="qa-notice" data-testid="qa-values-to-reenter">
                {reenterLine}
              </p>
            )}
            {buildFailure && buildFailureLine && (
              <div className="qa-build-failure" data-testid="qa-build-failure">
                <span className="qa-attention-line">{buildFailureLine}</span>
                <div className="qa-attention-actions">
                  <Button variant="secondary" disabled={!env.canRun} onClick={() => void env.run()}>
                    Check again
                  </Button>
                  {buildFailure.message && (
                    <details className="qa-output">
                      <summary>Show output</summary>
                      <pre>{buildFailure.message}</pre>
                    </details>
                  )}
                </div>
              </div>
            )}

            {startCard ? (
              <div className="blk" data-testid="environment-start">
                <div className="bh">
                  <span className="label">Set up</span>
                  <span className="fact">one run, then it keeps itself working</span>
                </div>
                <p className="body-p">
                  One run reads this repository, installs it, starts it in a disposable sandbox and
                  checks its first page, fixing what it can on the way. Usually a few dollars of
                  usage.
                </p>
                <span className="qa-hint">{RECORDING_NOTE}</span>
                <span className="qa-hint">{runHelp}</span>
                <span>
                  <button
                    type="button"
                    className="wrk-quiet-btn qa-disclosure"
                    aria-expanded={byHand}
                    onClick={() =>
                      setByHandFor((current) => (current === selected ? null : (selected ?? null)))
                    }
                  >
                    Configure by hand instead
                  </button>
                </span>
              </div>
            ) : (
              <>
                {(failed || awaitingSignIn) && (
                  <div className="stopbox qa-needs-box">
                    <div className="h">
                      <Stamp>needs you</Stamp>
                      {awaitingSignIn ? "Sign in to the app" : "The last run stopped"}
                    </div>
                    <p>
                      The checklist below carries the cause and the fix on the row where the run
                      stopped.
                    </p>
                  </div>
                )}
                <div className="blk" data-testid="environment-checklist">
                  <div className="bh">
                    <span className="label">Last run</span>
                    <span className="fact">what one run does, and how far the last one got</span>
                  </div>
                  {systemLine && (
                    <span className="qa-hint" data-testid="qa-run-system">
                      {systemLine}
                    </span>
                  )}
                  <ol className="wrk-preflight-list qa-checklist" aria-label="Setup checklist">
                    {rows.map((row) => {
                      const attention = attentionKey === row.key;
                      const detail = rowDetail(row, { attention, failed, working: view.working });
                      return (
                        <li key={row.key} data-state={row.state} className={ROW_CLASSES[row.state]}>
                          <span className="wrk-preflight-mark" aria-hidden="true">
                            {MARKS[row.state]}
                          </span>
                          <span className="wrk-preflight-text">
                            <span>{row.label}</span>
                            {detail && <span className="wrk-preflight-fix">{detail}</span>}
                            {row.state === "running" &&
                              validation?.message &&
                              validation.message !== detail && (
                                <span className="wrk-preflight-fix">{validation.message}</span>
                              )}
                            {row.key === "tests" &&
                              row.state === "warning" &&
                              validation?.suite?.outputTail && (
                                <details className="qa-output">
                                  <summary>Show output</summary>
                                  <pre>{validation.suite.outputTail}</pre>
                                </details>
                              )}
                            {attention && attentionBlock}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                  {!attentionPlaced && attentionBlock}
                  {steps.length > 0 && (
                    <>
                      <span className="qa-hint qa-steps-head">Steps</span>
                      <ol className="wrk-preflight-list qa-checklist" aria-label="Setup steps">
                        {steps.map((step) => {
                          // A step that ran out of time says so in place of its time.
                          const took = stepRanOutLine(step) ?? stepElapsed(step);
                          const line = typeof step.line === "number" ? step.line : null;
                          return (
                            <li
                              key={step.index}
                              data-state={step.state}
                              className={ROW_CLASSES[step.state]}
                            >
                              <span className="wrk-preflight-mark" aria-hidden="true">
                                {MARKS[step.state]}
                              </span>
                              <span className="wrk-preflight-text">
                                <span>{step.label}</span>
                                {took && <span className="wrk-preflight-fix">{took}</span>}
                                {step.state === "failed" && typeof step.exitCode === "number" && (
                                  <span className="wrk-preflight-fix">exit {step.exitCode}</span>
                                )}
                                {line !== null &&
                                  (env.yaml ? (
                                    <button
                                      type="button"
                                      className="wrk-quiet-btn qa-step-line"
                                      onClick={() => setReveal({ line, at: Date.now() })}
                                    >
                                      line {line}
                                    </button>
                                  ) : (
                                    <span className="wrk-preflight-fix">line {line}</span>
                                  ))}
                                {step.state === "failed" && step.outputTail && (
                                  <pre
                                    className="qa-step-tail"
                                    // Scrolls when long: a keyboard reaches it too.
                                    tabIndex={0}
                                    aria-label={`Output of ${step.label}`}
                                  >
                                    {step.outputTail}
                                  </pre>
                                )}
                              </span>
                            </li>
                          );
                        })}
                      </ol>
                    </>
                  )}
                  {runNotes.length > 0 && (
                    <ul className="qa-run-notes" aria-label="What the run noticed">
                      {runNotes.map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  )}
                </div>

                {whatRunsHere}

                <div className="blk" data-testid="environment-actions">
                  <div className="bh">
                    <span className="label">Run</span>
                    <span className="fact">reads the repository, boots it, and checks it</span>
                  </div>
                  <span className="qa-hint">{RECORDING_NOTE}</span>
                  <span className="qa-hint">{runHelp}</span>
                  {canSwitch && (
                    <span>
                      <Button variant="secondary" onClick={() => void env.switchTo()}>
                        Switch to this one
                      </Button>
                    </span>
                  )}
                  {/* Before any run, every run reads the repository anyway. */}
                  {latestRun && (
                    <label className="qa-reinspect">
                      <input
                        type="checkbox"
                        checked={env.reinspect}
                        disabled={busy || validating}
                        onChange={(event) => env.setReinspect(event.target.checked)}
                      />
                      <span>Re-read the repository first</span>
                    </label>
                  )}
                  <span className="qa-hint">
                    Usually a few dollars of usage. Stops after three fixes.
                  </span>
                  <span className="qa-hint">{SHORT_VALUES_NOTE}</span>
                  {(view.draft || view.active) && (
                    <div className="qa-validate-now">
                      <Button
                        variant="secondary"
                        disabled={!env.canCheck}
                        onClick={() => void env.check()}
                      >
                        Validate now
                      </Button>
                      <span className="qa-hint" data-testid="validate-now-help">
                        {env.checkHeldByEdits ? VALIDATE_NOW_HELD : VALIDATE_NOW_HELP}
                      </span>
                    </div>
                  )}
                  {latestRun && (
                    <div className="qa-runs-row">
                      <span className="qa-hint">
                        Run {latestRun.number} ·{" "}
                        {formatEnvironmentDate(validation?.startedAt ?? validation?.checkedAt)} ·{" "}
                        <button
                          type="button"
                          className="wrk-quiet-btn qa-disclosure"
                          aria-expanded={showRuns}
                          onClick={() => setShowRuns((open) => !open)}
                        >
                          previous runs
                        </button>
                      </span>
                      {showRuns && (
                        <ol className="qa-runs" aria-label="Previous runs">
                          {view.runs.map((entry) => (
                            <li key={entry.revisionId}>
                              <span>
                                Run {entry.number} · {formatEnvironmentDate(entry.startedAt)} ·{" "}
                                {RUN_STATUS_WORDS[entry.status]}
                              </span>
                              {entry.cause && <span className="qa-fact">{entry.cause}</span>}
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  )}
                  {env.statusPollStopped && (
                    <span role="status" className="qa-hint">
                      Environment updates are paused. Refresh status reads them again.
                    </span>
                  )}
                </div>
              </>
            )}
            {/* Builds on the inferred install can leave a record before any setup run. */}
            {startCard && capabilities && whatRunsHere}

            {env.yaml && (
              <EnvironmentYaml
                yaml={env.yaml}
                revisions={[view.draft, view.active]}
                busy={busy || validating}
                error={env.yamlError}
                onSave={env.saveYaml}
                onReset={() => void env.resetToInferred()}
                markLine={reveal?.line ?? failedLine}
                reveal={reveal}
              />
            )}

            {byHand && (
              <fieldset
                disabled={busy || validating}
                className="blk qa-fields qa-group"
                data-testid="repository-builds"
              >
                <legend className="sr-only">How it builds</legend>
                <div className="bh">
                  <span className="label">How it builds</span>
                  <span className="fact">what a sandbox needs to boot this repository</span>
                </div>
                <div className="wrk-settings">
                  <SettingsRow
                    section="application"
                    testId="qa-row-application"
                    label="Application"
                    summary={
                      config.setupCommands.filter(Boolean).length
                        ? inspectionSummary(config)
                        : "Nothing inferred yet"
                    }
                    open={openSection === "application"}
                    onToggle={toggle("application")}
                  >
                    <ApplicationFields
                      config={config}
                      repositories={env.repositories}
                      primaryRepoId={selected!}
                      onChange={env.edit}
                    />
                  </SettingsRow>
                  <SettingsRow
                    section="defined-in"
                    testId="qa-row-defined-in"
                    label="Defined in"
                    summary={environmentFromSummary(config, env.repositories)}
                    open={openSection === "defined-in"}
                    onToggle={toggle("defined-in")}
                  >
                    <EnvironmentFromFields
                      config={config}
                      repositories={env.repositories}
                      primaryRepoId={selected!}
                      onChange={env.edit}
                      onFind={env.findSibling}
                      onOpenRow={openRow}
                    />
                  </SettingsRow>
                  <SettingsRow
                    section="sign-in"
                    testId="qa-row-sign-in"
                    label="Sign-in"
                    summary={signInSummary(config)}
                    open={openSection === "sign-in"}
                    onToggle={toggle("sign-in")}
                  >
                    <SignInFields
                      config={config}
                      credentialNames={credentialNames}
                      onChange={env.edit}
                      onOpenRow={openRow}
                    />
                  </SettingsRow>
                  <SettingsRow
                    section="first-check"
                    testId="qa-row-first-check"
                    label="First check"
                    summary={firstCheckSummary(config)}
                    open={openSection === "first-check"}
                    onToggle={toggle("first-check")}
                  >
                    <FirstCheckFields config={config} onChange={env.edit} />
                  </SettingsRow>
                  <SettingsRow
                    section="credentials"
                    testId="qa-row-credentials"
                    label="Variables"
                    summary={repositoryVariablesSummary(variableRows)}
                    open={openSection === "credentials"}
                    onToggle={toggle("credentials")}
                  >
                    <RepositoryVariables
                      rows={variableRows}
                      workspaceError={workspace.error}
                      reloadWorkspace={workspace.reload}
                      ask={variableAsk}
                      name={env.secretName}
                      value={env.secretValue}
                      setName={env.setSecretName}
                      setValue={env.setSecretValue}
                      onStage={env.stageSecrets}
                      onToggle={env.toggleRemoved}
                    />
                  </SettingsRow>
                  <SettingsRow
                    section="network"
                    testId="qa-row-network"
                    label="Network"
                    summary={networkSummary(config)}
                    open={openSection === "network"}
                    onToggle={toggle("network")}
                  >
                    <NetworkFields config={config} onChange={env.edit} />
                  </SettingsRow>
                </div>
              </fieldset>
            )}
          </>
        )}

        {selected && repo && (
          <div className="blk qa-group" data-testid="repository-reads">
            <div className="bh">
              <span className="label">What agents read</span>
              <span className="fact">context loaded into every run on this repository</span>
            </div>
            <div className="wrk-settings">
              {ready && view && byHand && (
                <SettingsRow
                  section="other-repositories"
                  testId="qa-row-other-repositories"
                  label="Other repositories"
                  summary={otherRepositoriesSummary(config, env.repositories)}
                  open={openSection === "other-repositories"}
                  onToggle={toggle("other-repositories")}
                >
                  <fieldset disabled={busy || validating} className="qa-fields">
                    <legend className="sr-only">Other repositories</legend>
                    <OtherRepositories
                      config={config}
                      repositories={env.repositories}
                      primaryRepoId={selected}
                      onChange={env.edit}
                      onRefreshDependency={env.refreshDependency}
                      onStoreCredential={(name, value) => env.stageSecrets({ [name]: value })}
                    />
                  </fieldset>
                </SettingsRow>
              )}
              <SettingsRow
                section="skills"
                testId="qa-row-skills"
                label="Skills"
                summary={agentTrustSummary(agentTrust)}
                open={openSection === "skills"}
                onToggle={toggle("skills")}
              >
                <RepositoryAgentTrust state={agentTrust} />
              </SettingsRow>
              {/* An older server sends no index field: nothing to say then. */}
              {env.index !== undefined && (
                <SettingsRow
                  section="role"
                  testId="qa-row-role"
                  label="Role"
                  summary={roleSummary(index)}
                  open={openSection === "role"}
                  onToggle={toggle("role")}
                >
                  <RepositoryRole
                    key={selected}
                    index={index}
                    busy={env.indexBusy}
                    onSave={(next) => env.saveRole(selected, next)}
                  />
                </SettingsRow>
              )}
              {env.index !== undefined && env.relations !== undefined && (
                <SettingsRow
                  section="related"
                  testId="qa-row-related"
                  label="Related repositories"
                  summary={relationsSummary(env.relations)}
                  open={openSection === "related"}
                  onToggle={toggle("related")}
                >
                  <RepositoryRelations
                    key={selected}
                    view={env.relations}
                    repositories={env.repositories}
                    repoId={selected}
                    busy={env.relationsBusy}
                    error={env.relationsError}
                    onWrite={(write) => env.saveRelation(selected, write)}
                  />
                </SettingsRow>
              )}
              {env.index !== undefined && (
                <SettingsRow
                  section="description"
                  testId="qa-row-description"
                  label="Description"
                  summary={
                    description.kind === "indexing"
                      ? "Indexing"
                      : description.kind === "failed"
                        ? description.message
                        : (description.text ?? "No description yet.")
                  }
                  open={openSection === "description"}
                  onToggle={toggle("description")}
                >
                  <RepositoryDescription
                    key={selected}
                    index={index}
                    notSetUp={view?.repoId === selected && view.state === "not_set_up"}
                    busy={env.indexBusy}
                    error={env.indexError}
                    onReindex={() => void env.reindex(selected)}
                    onSave={(next) => env.saveDescription(selected, next)}
                  />
                </SettingsRow>
              )}
            </div>
          </div>
        )}

        {selected && repo && (
          <div className="blk qa-group" data-testid="repository-ships">
            <div className="bh">
              <span className="label">How it ships</span>
              <span className="fact">from a finished build to production</span>
            </div>
            <div className="wrk-settings">
              {checksSummary && (
                <SettingsRow
                  section="checks"
                  testId="qa-row-checks"
                  label="Checks"
                  summary={checksSummary}
                  open={openSection === "checks"}
                  onToggle={toggle("checks")}
                >
                  <CheckRequirements index={index} />
                </SettingsRow>
              )}
              <SettingsRow
                section="pull-requests"
                testId="qa-row-pull-requests"
                label="Pull requests"
                summary={prBaseSummary(prBase)}
                open={openSection === "pull-requests"}
                onToggle={toggle("pull-requests")}
              >
                <RepositoryPrBase repoId={selected} state={prBase} />
              </SettingsRow>
              <RepositoryDeploys repoId={selected} deploys={deploys} />
            </div>
          </div>
        )}

        {/* Renders nothing unless running copies are on for the workspace. */}
        {selected && repo && <RepositoryRunningSystem repoId={selected} />}

        {ready && view && byHand && (
          <fieldset disabled={busy || validating} className="qa-fields qa-group">
            <legend className="sr-only">Advanced</legend>
            <div className="wrk-settings">
              <SettingsRow
                section="advanced"
                testId="qa-row-advanced"
                label="Advanced"
                summary={advancedSummary(config)}
                open={openSection === "advanced"}
                onToggle={toggle("advanced")}
              >
                <EnvironmentAdvanced
                  config={config}
                  credentialNames={credentialNames}
                  onChange={env.edit}
                  onOpenRow={openRow}
                  onStoreCredential={(name, value) => env.stageSecrets({ [name]: value })}
                />
              </SettingsRow>
            </div>
          </fieldset>
        )}
      </div>
      {footer}
    </>
  );
}
